import { CaseUtil } from '@vectord/utils';
import pluralize from 'pluralize';

import { type TranslationEntity, translations } from '../data/translations';

interface TranslationResponseType {
  translations: Record<TranslationEntity, {
    displayName: string;
  }>;
}

interface TranslationOptions {
  pluralize?: number | boolean;
  capitalize?: boolean;
  truncate?: number;
  case?: 'constant' | 'kebab' | 'lower' | 'snake' | 'title';
  withArticle?: boolean;
}

const initialTranslations: TranslationResponseType['translations'] = translations;

class TranslationService {
  private translations: TranslationResponseType['translations'];

  constructor(newTranslations: TranslationResponseType['translations'] = initialTranslations) {
    this.translations = newTranslations;
  }

  /**
   * Check if the provided data is valid translations
   * @param data The data to check, fetched from BE
   * @returns Boolean indicating whether the data is valid
   */
  public static isValidTranslations(data: unknown): data is Record<TranslationEntity, { displayName: string }> {
    return data !== null && typeof data === 'object';
  }

  /**
   * Updates the translation data with the new translations fetched from BE.
   * This method replaces the existing translations in the service.
   * @param newTranslations - The new set of translations retrieved from the backend.
   * @returns void
   */
  updateTranslations(newTranslations: TranslationResponseType['translations']) {
    this.translations = newTranslations;
  }

  /**
   * Get display name for a specific entity
   * @param entityKey The key of the entity
   * @param options Additional options for display name generation
   * @returns Formatted display name
   */
  public get(
    entityKey: TranslationEntity,
    options: TranslationOptions = {},
  ): string {
    const {
      pluralize: shouldPluralize,
      capitalize,
      truncate,
      case: caseStyle,
      withArticle,
    } = options;

    // If translation exists, use it else use default
    const translation = this.translations[entityKey] ?? {
      displayName: entityKey,
    };
    let { displayName } = translation;

    if (typeof shouldPluralize === 'number') {
      displayName = pluralize(displayName, shouldPluralize);
    } else if (shouldPluralize) {
      displayName = pluralize(displayName);
    }

    if (capitalize) displayName = displayName.charAt(0).toUpperCase() + displayName.slice(1);
    if (truncate) displayName = displayName.slice(0, truncate);

    if (caseStyle) displayName = CaseUtil.toCase(`${caseStyle}Case`, displayName);

    if (withArticle) {
      const firstLetter = displayName.charAt(0).toLowerCase();
      const articleToAdd = ['a', 'e', 'i', 'o', 'u'].includes(firstLetter)
        ? 'an'
        : 'a';
      displayName = `${articleToAdd} ${displayName}`;
    }

    return displayName;
  }

  /**
   * Translate a template string containing entity placeholders
   * @param template The template string with placeholders in format #{entityKey}
   * @param entityOptions Array of options that will be applied to placeholders in order
   * @returns Translated string with replaced placeholders
   */
  public translate(
    template: string,
    entityOptions: (TranslationOptions | undefined)[] = [],
  ): string {
    let placeholderIndex = 0;
    return template.replace(/#{([a-zA-Z_]+)}/g, (_, entityKey) => {
      placeholderIndex += 1;
      return this.get(entityKey, entityOptions[placeholderIndex - 1]);
    });
  }

  /**
   * Get all available entity keys
   * @returns Array of entity keys
   */
  public getEntityKeys(): string[] {
    return Object.keys(this.translations);
  }

  /**
   * Check if a specific entity key exists
   * @param entityKey The key to check
   * @returns Boolean indicating existence of the key
   */
  public hasEntity(entityKey: string): boolean {
    return this.getEntityKeys().includes(entityKey);
  }

  /**
   * Generate a debug string representation of translations
   * @returns Formatted debug string
   */
  public debugPrint(): string {
    return Object.entries(this.translations)
      .map(([key, { displayName }]) => `${key}: ${displayName} (Plural: ${pluralize(displayName)})`)
      .join('\n');
  }
}

const translationService = new TranslationService();

const methodsToExport = Object.getOwnPropertyNames(TranslationService.prototype)
  .filter((method) => method !== 'constructor'
    && !method.startsWith('_')
    && typeof translationService[method as keyof TranslationService] === 'function') as (keyof TranslationService)[];

type MethodName = typeof methodsToExport[number];
type BoundMethods = {
  [K in MethodName]: TranslationService[K]
};

/**
 * When you destructure a class instance, the methods are not bound to the instance.
 * This is why we need to bind the methods to the instance while exporting them.
 */
const boundMethods = methodsToExport.reduce((acc, method) => ({
  ...acc,
  [method]: translationService[method].bind(translationService),
}), {} as BoundMethods);

export const {
  translate,
  get: getDisplayName,
  getEntityKeys,
  hasEntity,
  debugPrint,
  updateTranslations,
} = boundMethods;

export default translationService;

export { TranslationService };
